import React, { useState, useEffect } from 'react';
import { Send, Mail, Phone, MapPin, MessageSquare, CheckCircle, X } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setFormData({ name: '', email: '', message: '' });
      setIsSubmitting(false);
      setShowSuccessPopup(true);
    }, 1000);
  };

  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 5000); // Auto-hide after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup]);

  return (
    <section id="contact" className="py-20 px-4 bg-gradient-to-b from-white to-beige-50 relative">
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50">
          <div className="glass-strong rounded-2xl p-6 shadow-2xl border border-brown-200/50 max-w-md mx-4 animate-slide-down">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-brown-900 mb-1">
                  Message Sent Successfully!
                </h3>
                <p className="text-brown-700 text-sm">
                  Thank you for your message! We will get back to you soon.
                </p>
              </div>
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="flex-shrink-0 text-brown-600 hover:text-brown-900 transition-colors p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-brown-900 mb-4">
            Get in Touch
          </h2>
          <p className="text-xl text-brown-700 max-w-2xl mx-auto">
            Have questions or feedback? We'd love to hear from you
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-6">
            <div className="glass-strong rounded-2xl p-6">
              <h3 className="text-2xl font-bold text-brown-900 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-brown-600 to-brown-800 rounded-lg flex items-center justify-center text-white">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-brown-900 mb-1">Email</div>
                    <p className="text-brown-700 hover:text-brown-900 transition-colors">
                      info@sterling-spins.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-brown-600 to-brown-800 rounded-lg flex items-center justify-center text-white">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-brown-900 mb-1">Phone</div>
                    <p  className="text-brown-700 hover:text-brown-900 transition-colors">
                      +44 0973 385 308
                    </p>
                  </div>
                </div>

               
              </div>
            </div>

            
          </div>

          {/* Contact Form */}
          <div className="glass-strong rounded-3xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-brown-900 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  className="w-full px-4 py-3 glass rounded-xl border border-brown-200/50 focus:border-brown-400 focus:outline-none focus:ring-2 focus:ring-brown-400/20 text-brown-900 placeholder-brown-400 transition-all"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-brown-900 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 glass rounded-xl border border-brown-200/50 focus:border-brown-400 focus:outline-none focus:ring-2 focus:ring-brown-400/20 text-brown-900 placeholder-brown-400 transition-all"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-brown-900 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  autoComplete="off"
                  className="w-full px-4 py-3 glass rounded-xl border border-brown-200/50 focus:border-brown-400 focus:outline-none focus:ring-2 focus:ring-brown-400/20 text-brown-900 placeholder-brown-400 resize-none transition-all"
                  placeholder="Tell us how we can help..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-brown-700 to-brown-900 text-white rounded-xl font-semibold text-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
              >
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                {!isSubmitting && <Send className="w-5 h-5" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;

